import type { Module } from '@ezishizi/learning-core';
import {
  store,
  todayKey,
  type ActivitySummary,
  type SessionModule,
  type SessionRecord,
} from './store/index.ts';

/**
 * Progress key for one activity: a module on a set, or on the whole lesson. A word game on the
 * adventure islands adds the character, since the same game runs once per word of the set.
 */
export const activityKey = (
  lessonId: string,
  setId: string | null,
  module: SessionModule,
  character: string | null = null,
) => `${lessonId}:${setId ?? 'lesson'}:${module}${character ? `:${character}` : ''}`;

export interface ActivityProgress {
  /** ISO timestamp of the activity's most recent finish; `activityState` compares this to today's
   * date rather than keeping a separate set of days finished on. */
  lastFinishedAt: string;
  /** Best star rating across every session of this activity. */
  bestStars: number;
  /** Best first-try score (as a fraction) — what the test card reports; null when no session of
   * it had a maxScore to divide by. */
  bestRatio: number | null;
  count: number;
}

export type ProgressMap = Map<string, ActivityProgress>;

export type ActivityState = 'today' | 'before' | 'none';

export function activityState(p: ProgressMap, key: string): ActivityState {
  const a = p.get(key);
  if (!a) return 'none';
  return todayKey(new Date(a.lastFinishedAt)) === todayKey() ? 'today' : 'before';
}

/**
 * Turns the per-activity aggregate `store.getProgress` returns (the Worker's own query, or the
 * local store's `summariseSessions`) into the map every path/study screen keys its lookups by.
 */
export function toProgressMap(rows: ActivitySummary[]): ProgressMap {
  const map: ProgressMap = new Map();
  for (const r of rows) {
    map.set(activityKey(r.lessonId, r.setId, r.module as SessionModule, r.character), {
      lastFinishedAt: r.lastFinishedAt,
      bestStars: r.bestStars,
      bestRatio: r.bestRatio,
      count: r.count,
    });
  }
  return map;
}

/**
 * Folds one more finished session into a progress map, without a round trip to the store: the
 * result screen needs this for the session it just finished, whose save can still be in flight
 * when that screen mounts (see Result.tsx). Returns a new map; `map` itself is left untouched.
 */
export function foldSession(map: ProgressMap, s: SessionRecord): ProgressMap {
  const key = activityKey(s.lessonId, s.setId, s.module, s.character ?? null);
  const prev = map.get(key);
  const ratio = s.maxScore > 0 ? s.score / s.maxScore : null;
  const next: ActivityProgress = {
    lastFinishedAt: prev && prev.lastFinishedAt > s.finishedAt ? prev.lastFinishedAt : s.finishedAt,
    bestStars: Math.max(prev?.bestStars ?? 0, s.stars),
    bestRatio: ratio === null ? (prev?.bestRatio ?? null) : Math.max(prev?.bestRatio ?? 0, ratio),
    count: (prev?.count ?? 0) + 1,
  };
  const out = new Map(map);
  out.set(key, next);
  return out;
}

/**
 * Builds a progress map from a batch of sessions in one call, folding each in turn — mainly for
 * tests (path.test.ts stands up a `ProgressMap` from a list of finished stops this way) rather
 * than app code, which either loads the aggregate whole (`loadProgress`) or folds in one more
 * session at a time (`foldSession`).
 */
export function summarise(sessions: SessionRecord[]): ProgressMap {
  return sessions.reduce(foldSession, new Map());
}

/**
 * Everything the child has finished, summarised per activity, over every session ever recorded —
 * unlike `store.listSessions`, which the API caps at 200 per call, so a naive summary of just that
 * page would drop a child's earliest finished work once they passed the cap and mark it undone
 * again on the path.
 */
export async function loadProgress(profileId: string): Promise<ProgressMap> {
  return toProgressMap(await store.getProgress(profileId));
}
